import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { StatusBadge, fmtEUR } from "@/components/StatusBadge";
import MissionJournal from "@/components/MissionJournal";
import MissionAvenants from "@/components/MissionAvenants";
import { useAuth } from "@/lib/auth";
import { apiClient, readError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Lock, Send, Star, Hammer, FileText, ShieldCheck, Award, Download } from "lucide-react";

export default function MissionDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [mission, setMission] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [messages, setMessages] = useState([]);
  const [quoteForm, setQuoteForm] = useState({ amount: "", description: "", duration_days: "", deposit_percent: "" });
  const [msgText, setMsgText] = useState("");
  const [msgArtisanId, setMsgArtisanId] = useState("");
  const [artisanNames, setArtisanNames] = useState({});
  const [artisanInvoice, setArtisanInvoice] = useState(null);
  const [vatApplicable, setVatApplicable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [review, setReview] = useState({ rating: 5, comment: "" });
  const [journalState, setJournalState] = useState({ can_validate: false, finition_count: 0 });
  const [journalUpdates, setJournalUpdates] = useState([]);
  const [certificate, setCertificate] = useState(null);
  const [dispute, setDispute] = useState(null);
  const [showDisputeForm, setShowDisputeForm] = useState(false);
  const [disputeReason, setDisputeReason] = useState("");
  const [showMediatorContact, setShowMediatorContact] = useState(false);
  const [mediatorSubject, setMediatorSubject] = useState("");
  const [mediatorMessage, setMediatorMessage] = useState("");

  const loadDispute = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`/missions/${id}/dispute`);
      setDispute(data.dispute || null);
    } catch {
      setDispute(null);
    }
  }, [id]);

  const loadCertificate = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`/missions/${id}/certificate`);
      setCertificate(data.certificate || null);
    } catch {
      setCertificate(null);
    }
  }, [id]);

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`/missions/${id}`);
      setMission(data.mission);
      setQuotes(data.quotes || []);
    } catch (e) {
      toast.error(readError(e));
    }
  }, [id]);

  const loadMessages = useCallback(async (threadId) => {
    try {
      const qp = threadId ? `?artisan_id=${threadId}` : "";
      const m = await apiClient.get(`/messages/${id}${qp}`);
      setMessages(m.data.messages || []);
    } catch {
      setMessages([]);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // Dispute check only for parties on paid+ missions (avoids 403 noise for browsing artisans)
  useEffect(() => {
    if (!mission || !user) return;
    const isParty = user.id === mission.demandeur_id || user.id === mission.selected_artisan_id || user.role === "admin";
    if (isParty && ["paid", "in_progress", "completed"].includes(mission.status)) loadDispute();
  }, [mission, user, loadDispute]);

  // Messaging threads: demandeur/admin pick an artisan interlocutor; artisan gets his own thread
  useEffect(() => {
    if (!mission || !user) return;
    const ownerOrAdmin = user.id === mission.demandeur_id || user.role === "admin";
    if (!ownerOrAdmin) {
      loadMessages();
      return;
    }
    const def = msgArtisanId || mission.selected_artisan_id || quotes[0]?.artisan_id || (mission.invited_artisans || [])[0] || "";
    if (def && def !== msgArtisanId) {
      setMsgArtisanId(def);
      return;
    }
    loadMessages(def || undefined);
  }, [mission, quotes, user, msgArtisanId, loadMessages]);

  // Resolve names of invited artisans without a quote yet
  useEffect(() => {
    if (!mission || !user) return;
    if (user.id !== mission.demandeur_id && user.role !== "admin") return;
    if (!(mission.invited_artisans || []).length) return;
    apiClient.get("/artisans").then(({ data }) => {
      const map = {};
      (data.artisans || []).forEach((a) => {
        map[a.id] = a.company ? `${a.name} · ${a.company}` : a.name;
      });
      setArtisanNames(map);
    }).catch(() => {});
  }, [mission, user]);

  // Load existing artisan invoice for this mission
  useEffect(() => {
    if (!mission || !user) return;
    if (user.id !== mission.selected_artisan_id) return;
    apiClient.get("/invoices/me").then(({ data }) => {
      const inv = (data.invoices || []).find(
        (i) => i.type === "prestation_artisan" && i.related_mission_id === id && i.issuer_user_id === user.id
      );
      if (inv) setArtisanInvoice(inv);
    }).catch(() => {});
  }, [mission, user, id]);

  useEffect(() => {
    if (mission?.status === "completed") loadCertificate();
  }, [mission?.status, loadCertificate]);

  if (!mission || !user) return <div className="p-10 mono text-sm">Chargement…</div>;

  const isOwner = user.id === mission.demandeur_id;
  const isArtisan = user.role === "artisan";
  const isInvited = (mission.invited_artisans || []).includes(user.id) || (mission.invited_artisans || []).length === 0;
  const canQuote = isArtisan && isInvited && ["open", "quoted"].includes(mission.status);
  const myQuote = quotes.find((q) => q.artisan_id === user.id);

  const submitQuote = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await apiClient.post("/quotes", {
        mission_id: id,
        amount: parseFloat(quoteForm.amount),
        description: quoteForm.description,
        duration_days: parseInt(quoteForm.duration_days, 10),
        deposit_percent: quoteForm.deposit_percent ? parseInt(quoteForm.deposit_percent, 10) : null,
      });
      toast.success("Devis envoyé");
      setQuoteForm({ amount: "", description: "", duration_days: "", deposit_percent: "" });
      load();
    } catch (e) {
      toast.error(readError(e));
    } finally {
      setBusy(false);
    }
  };

  const acceptQuote = async (quoteId) => {
    setBusy(true);
    try {
      await apiClient.post(`/quotes/${quoteId}/accept`);
      toast.success("Devis accepté");
      load();
    } catch (e) {
      toast.error(readError(e));
    } finally {
      setBusy(false);
    }
  };

  const pay = async () => {
    setBusy(true);
    try {
      const acceptedQuote = quotes.find((q) => q.status === "accepted");
      if (!acceptedQuote) return;
      const { data } = await apiClient.post("/payments/checkout", {
        quote_id: acceptedQuote.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(readError(e));
      setBusy(false);
    }
  };

  const validateMission = async (applyPenalty = true) => {
    setBusy(true);
    try {
      const { data } = await apiClient.post(`/missions/${id}/validate`, { apply_penalty: applyPenalty });
      if (data.penalty_applied && data.penalty_amount > 0) {
        toast.success(`Fonds libérés. Pénalité retard appliquée : ${data.penalty_amount.toLocaleString("fr-FR")}€`);
      } else {
        toast.success("Fonds libérés à l'artisan ! Certificat émis.");
      }
      load();
      loadCertificate();
    } catch (e) {
      toast.error(readError(e));
    } finally {
      setBusy(false);
    }
  };

  const openDispute = async () => {
    if (disputeReason.trim().length < 10) {
      toast.error("Motif requis (10+ car.)");
      return;
    }
    setBusy(true);
    try {
      await apiClient.post(`/missions/${id}/dispute`, { reason: disputeReason.trim() });
      toast.success("Litige ouvert. L'équipe médiation Batisur a été notifiée.");
      setShowDisputeForm(false);
      setDisputeReason("");
      loadDispute();
    } catch (e) {
      toast.error(readError(e));
    } finally {
      setBusy(false);
    }
  };

  const respondDispute = async (accept) => {
    if (!dispute) return;
    setBusy(true);
    try {
      await apiClient.post(`/disputes/${dispute.id}/respond`, { accept });
      toast.success(accept ? "Proposition acceptée" : "Proposition refusée");
      loadDispute();
    } catch (e) {
      toast.error(readError(e));
    } finally {
      setBusy(false);
    }
  };

  const sendToMediator = async () => {
    if (mediatorSubject.trim().length < 2 || mediatorMessage.trim().length < 5) {
      toast.error("Sujet (2+ car.) et message (5+ car.) requis");
      return;
    }
    setBusy(true);
    try {
      await apiClient.post(`/missions/${id}/contact-mediator`, {
        subject: mediatorSubject.trim(),
        message: mediatorMessage.trim(),
      });
      toast.success(`Message envoyé à ${mission.mediator?.name}`);
      setShowMediatorContact(false);
      setMediatorSubject("");
      setMediatorMessage("");
    } catch (e) {
      toast.error(readError(e));
    } finally {
      setBusy(false);
    }
  };

  const issueArtisanInvoice = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post(`/missions/${id}/artisan-invoice`, { vat_applicable: vatApplicable });
      setArtisanInvoice(data.invoice);
      toast.success(data.already_exists ? "Facture déjà émise" : `Facture ${data.invoice.number} émise ✅`);
    } catch (e) {
      toast.error(readError(e));
    } finally {
      setBusy(false);
    }
  };

  // Load existing artisan invoice for this mission
  const downloadQuotePdf = async (q) => {
    try {
      const res = await apiClient.get(`/quotes/${q.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Devis_Batisur_DEV-${q.id.slice(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(readError(e));
    }
  };

  const downloadCertificate = async () => {
    try {
      const res = await apiClient.get(`/missions/${id}/certificate.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Certificat_Batisur_${certificate?.number || id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(readError(e));
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!msgText.trim()) return;
    const ownerOrAdmin = user.id === mission.demandeur_id || user.role === "admin";
    try {
      await apiClient.post("/messages", {
        mission_id: id,
        content: msgText,
        artisan_id: ownerOrAdmin ? msgArtisanId || undefined : undefined,
      });
      setMsgText("");
      loadMessages(ownerOrAdmin ? msgArtisanId || undefined : undefined);
    } catch (e) {
      toast.error(readError(e));
    }
  };

  const sendReview = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post("/reviews", {
        mission_id: id,
        rating: review.rating,
        comment: review.comment,
      });
      toast.success("Merci pour votre avis !");
      load();
    } catch (e) {
      toast.error(readError(e));
    }
  };

  const acceptedQuote = quotes.find((q) => q.status === "accepted");
  const moFeeBase = acceptedQuote?.amount ?? mission?.accepted_amount ?? 0;
  const moFee = mission?.mo_fee_amount ?? (mission?.mo_fee_type && Number(mission?.mo_fee_value) > 0
    ? (mission.mo_fee_type === "percent" ? moFeeBase * mission.mo_fee_value / 100 : Number(mission.mo_fee_value))
    : 0);

  return (
    <div className="min-h-screen bg-[#F7F7F9]">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-10">
        <button
          onClick={() => nav(-1)}
          className="mono text-[11px] uppercase tracking-widest text-zinc-500 hover:text-[#FF4D00]"
          data-testid="back-btn"
        >
          ← Retour
        </button>

        <div className="mt-4 border-2 border-[#0A0A0B] bg-white p-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="mono text-[11px] uppercase tracking-widest text-zinc-500">
                Mission #{mission.id.slice(0, 8).toUpperCase()} · {mission.category}
              </div>
              <h1 className="display text-4xl mt-2">{mission.title}</h1>
              <div className="mt-2 text-sm text-zinc-600">
                {mission.city}
                {mission.address && ` · ${mission.address}`}
                {mission.deadline && ` · échéance ${mission.deadline}`}
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <StatusBadge status={mission.status} />
              {(mission.budget_max >= 5000 || mission.accepted_amount >= 5000) && (
                <span className="badge-pill bg-emerald-50 text-emerald-700 border-emerald-300" title="Le SIRET de l'artisan doit être vérifié officiellement">
                  <ShieldCheck className="w-3 h-3" /> SIRET requis
                </span>
              )}
            </div>
          </div>
          <p className="mt-6 text-zinc-700 whitespace-pre-line">{mission.description}</p>

          <div className="mt-8 grid grid-cols-2 lg:grid-cols-5 gap-0 border-2 border-[#0A0A0B]">
            <Info label="Budget" value={`${fmtEUR(mission.budget_min)} – ${fmtEUR(mission.budget_max)}`} />
            <Info label="Surface" value={mission.surface_m2 ? `${mission.surface_m2} m²` : "—"} />
            <Info label="Montant retenu" value={mission.accepted_amount ? fmtEUR(mission.accepted_amount) : "—"} />
            <Info label="Demandeur" value={mission.demandeur_name} />
            <Info label="Devis reçus" value={quotes.length} />
          </div>

          {/* Mediator card — shown once mission is paid */}
          {mission.mediator && (
            <div className="mt-6 border-2 border-[#0A0A0B] bg-white shadow-[6px_6px_0_#FF4D00]" data-testid="mediator-card">
              <div className="p-5 flex items-center gap-5">
                <div
                  className="w-16 h-16 shrink-0 border-2 border-[#0A0A0B] flex items-center justify-center display text-2xl text-white"
                  style={{ backgroundColor: mission.mediator.color || "#FF4D00" }}
                  data-testid="mediator-avatar"
                >
                  {mission.mediator.initials || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="mono text-[10px] uppercase tracking-widest text-[#FF4D00]">// Votre médiateur Batisur</div>
                  <div className="display text-xl mt-1">
                    <span data-testid="mediator-name">{mission.mediator.name}</span>
                    <span className="text-sm text-zinc-500 ml-2">· {mission.mediator.title}</span>
                  </div>
                  {mission.mediator.bio && <div className="text-xs text-zinc-600 mt-1">{mission.mediator.bio}</div>}
                  <div className="mt-2 mono text-[10px] uppercase tracking-widest text-zinc-500">
                    Assigné le {(mission.mediator.assigned_at || "").slice(0, 10)} · Joignable 5j/7 sur ce chantier
                  </div>
                </div>
                {(user.id === mission.demandeur_id || user.id === mission.selected_artisan_id) && (
                  <button
                    onClick={() => setShowMediatorContact(true)}
                    className="btn-batisur text-xs flex items-center gap-1.5 shrink-0"
                    data-testid="contact-mediator-btn"
                  >
                    Contacter mon médiateur →
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Mediator contact modal */}
          {showMediatorContact && mission.mediator && (
            <div
              className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4"
              onClick={() => !busy && setShowMediatorContact(false)}
              data-testid="mediator-contact-modal"
            >
              <div
                className="bg-white border-2 border-[#0A0A0B] w-full max-w-xl shadow-[8px_8px_0_#FF4D00]"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="px-6 py-4 flex items-start justify-between" style={{ backgroundColor: mission.mediator.color || "#FF4D00", color: "white" }}>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 border-2 border-white/40 flex items-center justify-center display text-xl text-white">
                      {mission.mediator.initials}
                    </div>
                    <div>
                      <div className="mono text-[10px] uppercase tracking-widest text-white/80">// Contact direct</div>
                      <div className="display text-xl mt-1">Écrire à {mission.mediator.name}</div>
                    </div>
                  </div>
                  <button onClick={() => !busy && setShowMediatorContact(false)} className="text-white hover:text-[#0A0A0B]" data-testid="mediator-modal-close">
                    ✕
                  </button>
                </div>
                <div className="p-6 space-y-4">
                  <div className="border-2 border-[#0A0A0B] p-3 bg-zinc-50 text-xs mono uppercase tracking-widest">
                    Mission : <strong>{mission.title}</strong>
                  </div>
                  <div>
                    <label className="mono text-[10px] uppercase tracking-widest text-zinc-500">Sujet</label>
                    <input
                      type="text"
                      value={mediatorSubject}
                      onChange={(e) => setMediatorSubject(e.target.value)}
                      placeholder="Ex : Question sur le calendrier de validation"
                      className="w-full mt-1 border-2 border-[#0A0A0B] px-3 py-2 outline-none focus:border-[#FF4D00] text-sm"
                      data-testid="mediator-subject-input"
                    />
                  </div>
                  <div>
                    <label className="mono text-[10px] uppercase tracking-widest text-zinc-500">Votre message</label>
                    <textarea
                      value={mediatorMessage}
                      onChange={(e) => setMediatorMessage(e.target.value)}
                      placeholder="Bonjour, je vous contacte au sujet de ce chantier…"
                      rows={7}
                      className="w-full mt-1 border-2 border-[#0A0A0B] px-3 py-2 outline-none focus:border-[#FF4D00] resize-none text-sm"
                      data-testid="mediator-message-input"
                    />
                    <div className="mono text-[10px] uppercase tracking-widest text-zinc-400 mt-1">
                      {mediatorMessage.length} car. · Envoyé à {mission.mediator.name} + tracé dans votre dossier
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 pt-2">
                    <button onClick={() => !busy && setShowMediatorContact(false)} disabled={busy} className="btn-anthracite text-xs" data-testid="mediator-cancel">Annuler</button>
                    <button onClick={sendToMediator} disabled={busy} className="btn-batisur text-xs" data-testid="mediator-send">
                      {busy ? "Envoi…" : "Envoyer à mon médiateur"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Dispute banner (active) */}
          {dispute && dispute.status !== "resolved" && dispute.status !== "expired" && dispute.status !== "closed_admin" && (
            <div className="mt-6 border-2 border-[#FF4D00] bg-[#FFF3E5]" data-testid="dispute-active-banner">
              <div className="px-5 py-3 bg-[#FF4D00] text-white flex items-center gap-3">
                <ShieldCheck className="w-5 h-5" />
                <div className="mono text-[11px] uppercase tracking-widest">// Litige en cours · Médiation Batisur</div>
              </div>
              <div className="p-5">
                <div className="text-sm">
                  Ouvert par <strong>{dispute.opened_by_name}</strong> le {(dispute.created_at || "").slice(0, 10)}.
                  {" "}Statut : <strong>{dispute.status === "mediation" ? "en médiation" : "ouvert"}</strong>.
                  {" "}Deadline légale (30j) : <strong>{(dispute.deadline_at || "").slice(0, 10)}</strong>.
                </div>
                <div className="mt-3 border-l-4 border-[#FF4D00] pl-3 py-1 bg-white/50 text-sm italic">
                  &quot;{dispute.reason}&quot;
                </div>

                {dispute.admin_proposal && (
                  <div className="mt-4 border-2 border-[#0A0A0B] bg-white p-4">
                    <div className="mono text-[10px] uppercase tracking-widest text-[#FF4D00]">// Proposition médiation Batisur</div>
                    <div className="mt-2 grid grid-cols-2 gap-3 text-sm">
                      <div className="border border-zinc-200 p-2">
                        <span className="mono uppercase text-[9px] text-zinc-400">Remboursé au demandeur</span>
                        <div className="display text-2xl mt-1">{dispute.admin_proposal.demandeur_refund_pct}%</div>
                      </div>
                      <div className="border border-zinc-200 p-2">
                        <span className="mono uppercase text-[9px] text-zinc-400">Versé à l'artisan</span>
                        <div className="display text-2xl mt-1 text-[#FF4D00]">{dispute.admin_proposal.artisan_share_pct}%</div>
                      </div>
                    </div>
                    <div className="mt-3 text-sm whitespace-pre-wrap">{dispute.admin_proposal.text}</div>

                    {/* Response state per party */}
                    {(() => {
                      const isDem = user.id === mission.demandeur_id;
                      const isArt = user.id === mission.selected_artisan_id;
                      const myResp = isDem ? dispute.demandeur_response : isArt ? dispute.artisan_response : null;
                      if (!isDem && !isArt) return null;
                      if (myResp) {
                        return (
                          <div className="mt-4 mono text-[11px] uppercase tracking-widest text-zinc-600">
                            Votre réponse : <strong className={myResp.accepted ? "text-emerald-700" : "text-red-700"}>{myResp.accepted ? "✓ Acceptée" : "✗ Refusée"}</strong>
                          </div>
                        );
                      }
                      return (
                        <div className="mt-4 flex gap-2">
                          <button
                            onClick={() => respondDispute(true)}
                            disabled={busy}
                            className="btn-batisur flex items-center gap-2 text-xs"
                            data-testid="dispute-accept"
                          >
                            <CheckCircle2 className="w-4 h-4" /> Accepter la proposition
                          </button>
                          <button
                            onClick={() => respondDispute(false)}
                            disabled={busy}
                            className="btn-anthracite text-xs"
                            data-testid="dispute-refuse"
                          >
                            Refuser
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Open dispute CTA (only if paid/in_progress and no active dispute) */}
          {(mission.status === "paid" || mission.status === "in_progress") &&
            (user.id === mission.demandeur_id || user.id === mission.selected_artisan_id) &&
            (!dispute || dispute.status === "resolved" || dispute.status === "expired" || dispute.status === "closed_admin") && (
              <div className="mt-4 text-right">
                {!showDisputeForm ? (
                  <button
                    onClick={() => setShowDisputeForm(true)}
                    className="text-xs mono uppercase tracking-widest text-zinc-500 hover:text-[#FF4D00] underline"
                    data-testid="open-dispute-btn"
                  >
                    Signaler un litige →
                  </button>
                ) : (
                  <div className="border-2 border-[#0A0A0B] bg-white p-5 text-left">
                    <div className="mono text-[10px] uppercase tracking-widest text-[#FF4D00]">// Ouverture d'un litige</div>
                    <div className="display text-xl mt-1">Décrivez le désaccord</div>
                    <textarea
                      value={disputeReason}
                      onChange={(e) => setDisputeReason(e.target.value)}
                      placeholder="Expliquez clairement le motif du litige (malfaçon, retard, refus de validation, désaccord sur avenant…). Ces éléments seront transmis à l'admin Batisur pour médiation."
                      rows={5}
                      className="w-full mt-3 border-2 border-[#0A0A0B] px-3 py-2 outline-none focus:border-[#FF4D00] resize-none text-sm"
                      data-testid="dispute-reason-input"
                    />
                    <div className="mt-3 flex gap-2 justify-end">
                      <button onClick={() => { setShowDisputeForm(false); setDisputeReason(""); }} className="btn-anthracite text-xs" data-testid="dispute-cancel">Annuler</button>
                      <button onClick={openDispute} disabled={busy} className="btn-batisur text-xs" data-testid="dispute-submit">Ouvrir le litige</button>
                    </div>
                  </div>
                )}
              </div>
            )}

          {/* Action zone for demandeur */}
          {isOwner && acceptedQuote && mission.status !== "paid" && mission.status !== "completed" && (
            <div className="mt-6 border-2 border-[#FF4D00] p-6 flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="display text-2xl">Devis prêt au paiement séquestré</div>
                <div className="text-sm text-zinc-600 mt-1">
                  {acceptedQuote.deposit_percent ? (
                    <>
                      Acompte à séquestrer : <strong>{fmtEUR((acceptedQuote.amount + moFee) * acceptedQuote.deposit_percent / 100)}</strong> ({acceptedQuote.deposit_percent}%)
                      {" · "}Solde de <strong>{fmtEUR((acceptedQuote.amount + moFee) * (100 - acceptedQuote.deposit_percent) / 100)}</strong> à séquestrer avant validation
                    </>
                  ) : (
                    <>Montant à séquestrer : <strong>{fmtEUR(acceptedQuote.amount + moFee)}</strong></>
                  )}
                  {moFee > 0 && (
                    <div className="mt-1 text-xs text-zinc-600" data-testid="mo-fee-line">
                      dont vos honoraires donneur d'ordre : <strong>{fmtEUR(moFee)}</strong> — reversés à la validation
                    </div>
                  )}
                </div>
              </div>
              <button onClick={pay} disabled={busy} className="btn-batisur flex items-center gap-2" data-testid="pay-escrow">
                <Lock className="w-4 h-4" /> {acceptedQuote.deposit_percent ? "Payer l'acompte" : "Payer et séquestrer"}
              </button>
            </div>
          )}

          {/* Balance payment for split escrow */}
          {isOwner && mission.status === "paid" && mission.deposit_percent && !mission.balance_paid_at && (
            <div className="mt-6 border-2 border-[#FF4D00] bg-orange-50 p-6 flex flex-wrap items-center justify-between gap-4" data-testid="pay-balance-banner">
              <div>
                <div className="display text-2xl">Solde à séquestrer</div>
                <div className="text-sm text-zinc-700 mt-1">
                  Acompte de {mission.deposit_percent}% déjà séquestré. Il reste{" "}
                  <strong>{fmtEUR(((mission.accepted_amount || 0) + moFee) * (100 - mission.deposit_percent) / 100)}</strong> à sécuriser —
                  obligatoire avant la validation finale des travaux.
                </div>
              </div>
              <button onClick={pay} disabled={busy} className="btn-batisur flex items-center gap-2" data-testid="pay-balance">
                <Lock className="w-4 h-4" /> Payer le solde
              </button>
            </div>
          )}

          {isOwner && mission.status === "paid" && (() => {
            const deadline = mission.deadline ? new Date(mission.deadline) : null;
            const isLate = deadline && new Date() > deadline && mission.late_penalty_enabled;
            const daysLate = isLate ? Math.floor((Date.now() - deadline.getTime()) / 86400000) : 0;
            const amount = mission.accepted_amount || 0;
            const rate = mission.late_penalty_rate_per_day || 0.001;
            const cap = mission.late_penalty_cap_percent || 0.05;
            const estimatedPenalty = isLate ? Math.min(daysLate * rate * amount, cap * amount) : 0;
            return (
              <div className={`mt-6 border-2 p-6 ${
                journalState.can_validate
                  ? (isLate ? "border-[#FF4D00] bg-orange-50" : "border-emerald-600 bg-emerald-50")
                  : "border-amber-500 bg-amber-50"
              }`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className={`display text-2xl ${
                      journalState.can_validate
                        ? (isLate ? "text-[#0A0A0B]" : "text-emerald-900")
                        : "text-amber-900"
                    }`}>
                      {journalState.can_validate ? "Fonds séquestrés — Validation possible" : "Fonds séquestrés — En attente des preuves"}
                    </div>
                    <div className={`text-sm mt-1 ${
                      journalState.can_validate
                        ? (isLate ? "text-[#0A0A0B]" : "text-emerald-800")
                        : "text-amber-800"
                    }`}>
                      {journalState.can_validate
                        ? `${journalState.finition_count} preuve(s) de finition publiée(s) par l'artisan.`
                        : "L'artisan doit publier au moins une photo \"Finition\" dans le journal de chantier avant que vous puissiez valider."}
                    </div>
                    {isLate && (
                      <div className="mt-3 p-3 border-2 border-[#FF4D00] bg-white text-sm" data-testid="late-penalty-info">
                        <div className="mono text-[10px] uppercase tracking-widest text-[#FF4D00] font-semibold">
                          ⚠ Retard détecté · {daysLate} jour{daysLate > 1 ? "s" : ""}
                        </div>
                        <div className="mt-1">
                          Pénalité estimée : <strong>{estimatedPenalty.toLocaleString("fr-FR", { maximumFractionDigits: 2 })}€</strong>
                          {" "}({(rate * 100).toFixed(2)}%/jour, plafond {(cap * 100).toFixed(0)}%)
                          {" — "}reversés au demandeur, déduits du payout artisan.
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => validateMission(true)}
                      disabled={busy || !journalState.can_validate}
                      className={`px-6 py-3 font-semibold uppercase text-sm tracking-wider transition ${
                        journalState.can_validate
                          ? (isLate ? "bg-[#FF4D00] hover:bg-[#FF6700] text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white")
                          : "bg-zinc-200 text-zinc-400 cursor-not-allowed"
                      }`}
                      data-testid="validate-btn"
                      title={!journalState.can_validate ? "Photo de Finition requise dans le journal de chantier" : ""}
                    >
                      {journalState.can_validate ? (
                        isLate
                          ? <><CheckCircle2 className="w-4 h-4 inline mr-2" /> Valider avec pénalité</>
                          : <><CheckCircle2 className="w-4 h-4 inline mr-2" /> Valider et libérer</>
                      ) : (
                        <><Lock className="w-4 h-4 inline mr-2" /> Verrouillé — preuve requise</>
                      )}
                    </button>
                    {isLate && journalState.can_validate && (
                      <button
                        onClick={() => validateMission(false)}
                        disabled={busy}
                        className="px-6 py-2 text-xs mono uppercase tracking-widest border-2 border-zinc-400 text-zinc-700 hover:bg-zinc-100"
                        data-testid="validate-waive-btn"
                      >
                        Valider sans appliquer la pénalité
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {mission.status === "completed" && (
            <div className="mt-6 border-2 border-emerald-700 bg-emerald-50 p-6">
              <div className="display text-2xl text-emerald-900">Mission terminée ✓</div>
              <div className="grid grid-cols-3 gap-0 border-2 border-emerald-700 mt-4">
                <Info label="Payout artisan" value={fmtEUR(mission.artisan_payout)} />
                <Info label="Commission Batisur" value={fmtEUR(mission.total_commission)} />
                <Info label="Validée" value={mission.validated_at?.slice(0, 10)} />
              </div>
              {(mission.mo_fee_amount || 0) > 0 && user.id === mission.demandeur_id && (
                <div className="mt-3 border-2 border-emerald-700 bg-white p-3 text-sm" data-testid="mo-fee-released">
                  <strong>Vos honoraires donneur d'ordre libérés :</strong> {fmtEUR(mission.mo_fee_amount)} — reversés avec la libération du séquestre (0% commission Batisur).
                  <div className="text-xs text-zinc-600 mt-1">Votre facture d'honoraires Factur-X a été générée automatiquement — retrouvez-la dans « Mes factures » sur votre tableau de bord.</div>
                </div>
              )}
              {(mission.penalty_applied || mission.penalty_waived) && (
                <div className="mt-3 border-2 border-[#FF4D00] bg-white p-3 text-sm" data-testid="penalty-applied-banner">
                  {mission.penalty_applied ? (
                    <>
                      <strong>Pénalité retard appliquée :</strong> {fmtEUR(mission.penalty_amount)} ({mission.late_days} jour{mission.late_days > 1 ? "s" : ""} de retard) — reversés au demandeur.
                    </>
                  ) : (
                    <><strong>Retard de {mission.late_days} jour{mission.late_days > 1 ? "s" : ""}</strong> — pénalité levée par le demandeur (validation sans application).</>
                  )}
                </div>
              )}

              {/* Certificate banner */}
              <div className="mt-6 border-2 border-[#0A0A0B] bg-white p-5 flex flex-wrap items-center justify-between gap-4 shadow-[4px_4px_0_#0A0A0B]" data-testid="certificate-banner">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 border-2 border-[#0A0A0B] bg-[#FF4D00] flex items-center justify-center flex-shrink-0">
                    <Award className="w-6 h-6 text-white" strokeWidth={2.5} />
                  </div>
                  <div>
                    <div className="display text-xl text-[#0A0A0B]">Certificat de bonne fin de travaux</div>
                    <div className="text-sm text-zinc-700 mt-1">
                      Document PDF signé électroniquement par les deux parties.
                      Présentable à votre <strong>assureur (décennale)</strong> ou lors d'une <strong>revente immobilière</strong>.
                    </div>
                    {certificate?.number && (
                      <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mt-2">
                        N° {certificate.number} · Émis le {certificate.issued_at?.slice(0, 10)}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={downloadCertificate}
                  disabled={!certificate}
                  className="btn-anthracite flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="certificate-download-btn"
                >
                  <Download className="w-4 h-4" /> Télécharger le certificat
                </button>
              </div>
            </div>
          )}
        </div>

        {/* FACTURE CLIENT ARTISAN */}
        {user.id === mission.selected_artisan_id && ["paid", "in_progress", "completed"].includes(mission.status) && (
          <div className="mt-8 border-2 border-[#0A0A0B] bg-white p-5 flex flex-wrap items-center justify-between gap-4" data-testid="artisan-invoice-block">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 border-2 border-[#0A0A0B] bg-[#0A0A0B] flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6 text-white" strokeWidth={2.5} />
              </div>
              <div>
                <div className="display text-xl">Ma facture client (Factur-X)</div>
                <div className="text-sm text-zinc-700 mt-1">
                  Émettez votre facture de prestation à votre nom — format électronique <strong>Factur-X</strong> conforme 2026,
                  envoyée au tableau de bord de votre client.
                </div>
                {artisanInvoice ? (
                  <div className="mono text-[10px] uppercase tracking-widest text-emerald-700 mt-2" data-testid="artisan-invoice-number">
                    ✓ Émise — N° {artisanInvoice.number} · {Number(artisanInvoice.total_ttc).toLocaleString("fr-FR")} € TTC
                  </div>
                ) : (
                  <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={vatApplicable}
                      onChange={(e) => setVatApplicable(e.target.checked)}
                      data-testid="artisan-invoice-vat"
                    />
                    Je suis assujetti à la TVA (20%) — sinon mention « TVA non applicable, art. 293 B du CGI »
                  </label>
                )}
              </div>
            </div>
            {artisanInvoice ? (
              <a
                href={`${BACKEND_URL}/api/invoices/${artisanInvoice.id}/pdf`}
                className="btn-anthracite flex items-center gap-2"
                data-testid="artisan-invoice-download"
              >
                <Download className="w-4 h-4" /> Télécharger (Factur-X)
              </a>
            ) : (
              <button
                onClick={issueArtisanInvoice}
                disabled={busy}
                className="btn-batisur flex items-center gap-2 disabled:opacity-60"
                data-testid="artisan-invoice-create"
              >
                <FileText className="w-4 h-4" /> Émettre ma facture
              </button>
            )}
          </div>
        )}

        {/* JOURNAL DE CHANTIER — visible dès la publication (photos "Avant travaux" du demandeur) */}
        <div className="mt-8">
          <MissionJournal
              missionId={id}
              mission={mission}
              user={user}
              onUpdated={(state, items) => {
                setJournalState(state);
                if (items) setJournalUpdates(items);
              }}
            />
          </div>

        {/* AVENANTS */}
        {["paid", "completed"].includes(mission.status) && (
          <div className="mt-8">
            <MissionAvenants
              missionId={id}
              mission={mission}
              user={user}
              journalUpdates={journalUpdates}
            />
          </div>
        )}

        {/* QUOTES */}
        <div className="mt-8 border-2 border-[#0A0A0B] bg-white">
          <div className="border-b-2 border-[#0A0A0B] px-6 py-4 flex items-center justify-between">
            <div className="display text-2xl flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#FF4D00]" /> Devis
            </div>
            <div className="mono text-[11px] uppercase tracking-widest text-zinc-500">{quotes.length}</div>
          </div>
          {quotes.length === 0 && (
            <div className="p-8 text-center text-sm text-zinc-500">Aucun devis pour le moment.</div>
          )}
          {quotes.map((q) => (
            <div
              key={q.id}
              className="px-6 py-5 border-b border-zinc-200 last:border-b-0 grid grid-cols-12 gap-4 items-center"
              data-testid={`quote-${q.id}`}
            >
              <div className="col-span-12 lg:col-span-5">
                <div className="display text-lg">{q.artisan_name || "Artisan"}</div>
                {q.artisan_company && <div className="text-xs text-zinc-500">{q.artisan_company}</div>}
                <p className="text-sm text-zinc-700 mt-2 whitespace-pre-line">{q.description}</p>
              </div>
              <div className="col-span-6 lg:col-span-2">
                <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">Montant</div>
                <div className="display text-xl">{fmtEUR(q.amount)}</div>
                {q.deposit_percent ? (
                  <div className="mono text-[10px] uppercase tracking-widest text-[#FF4D00] mt-1" data-testid={`quote-deposit-info-${q.id}`}>
                    Acompte {q.deposit_percent}%
                  </div>
                ) : null}
              </div>
              <div className="col-span-6 lg:col-span-2">
                <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">Durée</div>
                <div className="display text-xl">{q.duration_days}j</div>
              </div>
              <div className="col-span-12 lg:col-span-3 flex flex-col items-end gap-2">
                <StatusBadge status={q.status === "accepted" ? "validated" : q.status === "rejected" ? "cancelled" : "open"} />
                {isOwner && q.status === "pending" && (
                  <button
                    onClick={() => acceptQuote(q.id)}
                    className="btn-anthracite text-xs"
                    disabled={busy}
                    data-testid={`accept-quote-${q.id}`}
                  >
                    Accepter ce devis
                  </button>
                )}
                {(isOwner || q.artisan_id === user.id || user.role === "admin") && (
                  <button
                    onClick={() => downloadQuotePdf(q)}
                    className="mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#FF4D00] underline flex items-center gap-1"
                    data-testid={`quote-pdf-${q.id}`}
                  >
                    <Download className="w-3 h-3" /> Devis PDF
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* SEND QUOTE (artisan) */}
        {canQuote && !myQuote && (
          <form
            onSubmit={submitQuote}
            className="mt-8 border-2 border-[#0A0A0B] bg-white p-8"
            data-testid="quote-form"
          >
            <div className="display text-2xl flex items-center gap-2">
              <Hammer className="w-5 h-5 text-[#FF4D00]" /> Envoyer un devis
            </div>
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div>
                <label className="mono text-[11px] uppercase tracking-widest text-zinc-600">Montant TTC (€)</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={quoteForm.amount}
                  onChange={(e) => setQuoteForm({ ...quoteForm, amount: e.target.value })}
                  className="w-full mt-2 border-2 border-[#0A0A0B] px-4 py-3 bg-white"
                  data-testid="quote-amount"
                />
              </div>
              <div>
                <label className="mono text-[11px] uppercase tracking-widest text-zinc-600">Durée (jours)</label>
                <input
                  type="number"
                  required
                  value={quoteForm.duration_days}
                  onChange={(e) => setQuoteForm({ ...quoteForm, duration_days: e.target.value })}
                  className="w-full mt-2 border-2 border-[#0A0A0B] px-4 py-3 bg-white"
                  data-testid="quote-duration"
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="mono text-[11px] uppercase tracking-widest text-zinc-600">Modalités de paiement</label>
              <select
                value={quoteForm.deposit_percent}
                onChange={(e) => setQuoteForm({ ...quoteForm, deposit_percent: e.target.value })}
                className="w-full mt-2 border-2 border-[#0A0A0B] px-4 py-3 bg-white"
                data-testid="quote-deposit"
              >
                <option value="">100% séquestré à la commande (défaut)</option>
                <option value="30">Acompte 30% séquestré, solde avant validation</option>
                <option value="40">Acompte 40% séquestré, solde avant validation</option>
                <option value="50">Acompte 50% séquestré, solde avant validation</option>
              </select>
            </div>
            <div className="mt-4">
              <label className="mono text-[11px] uppercase tracking-widest text-zinc-600">Détail des prestations</label>
              <textarea
                rows="4"
                required
                value={quoteForm.description}
                onChange={(e) => setQuoteForm({ ...quoteForm, description: e.target.value })}
                className="w-full mt-2 border-2 border-[#0A0A0B] px-4 py-3 bg-white"
                data-testid="quote-description"
              />
            </div>
            <button type="submit" disabled={busy} className="btn-batisur mt-4" data-testid="quote-submit">
              {busy ? "Envoi…" : "Soumettre le devis"}
            </button>
          </form>
        )}

        {/* MESSAGES */}
        {(() => {
          const ownerOrAdmin = isOwner || user.role === "admin";
          const interMap = new Map();
          quotes.forEach((q) => {
            interMap.set(q.artisan_id, q.artisan_name ? (q.artisan_company ? `${q.artisan_name} · ${q.artisan_company}` : q.artisan_name) : "Artisan");
          });
          (mission.invited_artisans || []).forEach((aid) => {
            if (!interMap.has(aid)) interMap.set(aid, artisanNames[aid] || `Artisan ${aid.slice(0, 6)}`);
          });
          const interlocutors = [...interMap.entries()].map(([aid, label]) => ({ id: aid, label }));
          const noInterlocutor = ownerOrAdmin && interlocutors.length === 0;
          return (
            <div className="mt-8 border-2 border-[#0A0A0B] bg-white">
              <div className="border-b-2 border-[#0A0A0B] px-6 py-4 flex flex-wrap items-center justify-between gap-3">
                <div className="display text-2xl">Messagerie</div>
                {ownerOrAdmin && interlocutors.length > 1 && (
                  <select
                    value={msgArtisanId}
                    onChange={(e) => setMsgArtisanId(e.target.value)}
                    className="border-2 border-[#0A0A0B] px-3 py-2 text-sm bg-white"
                    data-testid="message-thread-select"
                  >
                    {interlocutors.map((it) => (
                      <option key={it.id} value={it.id}>{it.label}</option>
                    ))}
                  </select>
                )}
                {ownerOrAdmin && interlocutors.length === 1 && (
                  <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">
                    Avec {interlocutors[0].label}
                  </div>
                )}
              </div>
              <div className="max-h-96 overflow-auto p-6 space-y-3 scrollbar-thin">
                {messages.length === 0 && (
                  <div className="text-sm text-zinc-500 text-center py-6">
                    {noInterlocutor
                      ? "La messagerie s'active dès qu'un artisan est invité ou envoie un devis."
                      : "Aucun message."}
                  </div>
                )}
                {messages.map((m) => (
                  <div
                    key={m.id}
                    className={`max-w-[85%] p-3 ${m.sender_id === user.id ? "ml-auto bg-[#0A0A0B] text-white" : "bg-zinc-100"}`}
                  >
                    <div className="mono text-[10px] uppercase tracking-widest opacity-70">
                      {m.sender_name} · {m.sender_role}
                    </div>
                    <div className="text-sm mt-1 whitespace-pre-line">{m.content}</div>
                  </div>
                ))}
              </div>
              {!noInterlocutor && (
                <form onSubmit={sendMessage} className="border-t-2 border-[#0A0A0B] p-4 flex gap-3" data-testid="message-form">
                  <input
                    value={msgText}
                    onChange={(e) => setMsgText(e.target.value)}
                    placeholder="Écrire un message…"
                    className="flex-1 border-2 border-[#0A0A0B] px-4 py-3"
                    data-testid="message-input"
                  />
                  <button type="submit" className="btn-batisur flex items-center gap-2" data-testid="message-send">
                    <Send className="w-4 h-4" /> Envoyer
                  </button>
                </form>
              )}
            </div>
          );
        })()}

        {/* REVIEW */}
        {isOwner && mission.status === "completed" && (
          <form onSubmit={sendReview} className="mt-8 border-2 border-[#0A0A0B] bg-white p-8" data-testid="review-form">
            <div className="display text-2xl flex items-center gap-2">
              <Star className="w-5 h-5 text-[#FF4D00]" /> Évaluation de l'artisan
            </div>
            <div className="flex gap-2 mt-4">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setReview({ ...review, rating: n })}
                  className={`w-12 h-12 border-2 flex items-center justify-center transition ${
                    n <= review.rating ? "bg-[#FF4D00] text-white border-[#FF4D00]" : "bg-white text-zinc-600 border-[#0A0A0B]"
                  }`}
                  data-testid={`rating-${n}`}
                >
                  <Star className="w-5 h-5" fill={n <= review.rating ? "currentColor" : "none"} />
                </button>
              ))}
            </div>
            <textarea
              rows="3"
              value={review.comment}
              onChange={(e) => setReview({ ...review, comment: e.target.value })}
              placeholder="Votre retour sur la mission…"
              className="w-full mt-4 border-2 border-[#0A0A0B] px-4 py-3 bg-white"
              data-testid="review-comment"
            />
            <button type="submit" className="btn-batisur mt-4" data-testid="review-submit">
              Publier l'avis
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="p-4 border-r-2 last:border-r-0 border-[#0A0A0B]">
      <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="display text-lg mt-1">{value}</div>
    </div>
  );
}
